
import { useState } from "react";
// next/image replaced with img;
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils/cn";
import { VideoPlayer } from "@/components/media/VideoPlayer";
import type { MediaItem } from "@/types";

interface PostMediaPreviewProps {
  media: MediaItem[];
  postUrl: string;
  maxItems?: number;
}

export function PostMediaPreview({ media, postUrl, maxItems = 4 }: PostMediaPreviewProps) {
  const [failed, setFailed] = useState(new Set<string>());
  const visible = media.slice(0, maxItems);
  const overflow = media.length - maxItems;

  if (media.length === 0) return null;

  // Single video
  if (media.length === 1 && media[0].type === "video") {
    return (
      <VideoPlayer
        src={media[0].url}
        poster={media[0].thumbnail_url || undefined}
        className="w-full max-h-[480px]"
      />
    );
  }

  // Single image
  if (media.length === 1 && media[0].type === "image") {
    return (
      <Link to={postUrl} className="block">
        <div
          className="relative rounded-xl overflow-hidden bg-muted"
          style={{ aspectRatio: `${media[0].width || 16}/${media[0].height || 9}`, maxHeight: "480px" }}
        >
          {!failed.has(media[0].id) ? (
            <img
              src={media[0].url}
              alt={media[0].alt_text || ""}
              className="object-cover"
              onError={() => setFailed((f) => new Set([...f, media[0].id]))}
              sizes="(max-width: 680px) 100vw, 680px"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-text-subtle text-sm">
              Image unavailable
            </div>
          )}
        </div>
      </Link>
    );
  }

  // Multi-image grid
  return (
    <Link to={postUrl} className="block">
      <div
        className={cn(
          "grid gap-1 rounded-xl overflow-hidden",
          visible.length === 2 && "grid-cols-2",
          visible.length >= 3 && "grid-cols-2",
        )}
        style={{ maxHeight: "400px" }}
      >
        {visible.map((item, idx) => (
          <div
            key={item.id}
            className={cn(
              "relative bg-muted overflow-hidden",
              visible.length === 3 && idx === 0 && "row-span-2",
            )}
            style={{ aspectRatio: visible.length <= 2 ? "1/1" : idx === 0 && visible.length === 3 ? "1/2" : "1/1" }}
          >
            {item.type === "image" && !failed.has(item.id) ? (
              <img
                src={item.url}
                alt={item.alt_text || ""}
                className="object-cover hover:scale-105 transition-transform duration-300"
                onError={() => setFailed((f) => new Set([...f, item.id]))}
                sizes="(max-width: 680px) 50vw, 340px"
              />
            ) : item.type === "video" ? (
              <div className="absolute inset-0 flex items-center justify-center bg-black">
                <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                  <svg className="w-5 h-5 text-white ml-1" viewBox="0 0 24 24" fill="currentColor">
                    <polygon points="5 3 19 12 5 21 5 3" />
                  </svg>
                </div>
              </div>
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-text-subtle text-xs">
                Image unavailable
              </div>
            )}

            {/* Overflow indicator on last visible item */}
            {idx === maxItems - 1 && overflow > 0 && (
              <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                <span className="text-white font-bold text-xl">+{overflow}</span>
              </div>
            )}
          </div>
        ))}
      </div>
    </Link>
  );
}
